import React, {useCallback, useEffect, useState} from 'react'
import {Box, Checkbox, Typography} from '@mui/material'
import moment from 'moment'
import DataTable, {ColumnDef, PaginationState} from 'src/components-new/data/DataTable'
import Filters, {FilterItem, FilterResult} from 'src/components-new/data/Filters'
import InlineMessage from 'src/components-new/feedback/InlineMessage'
import ActionIconButton from 'src/components-new/primitives/ActionIconButton'
import Button from 'src/components-new/primitives/Button'
import {BODY_PARTS_OPTIONS, STATUS} from 'src/constants/constants'
import {useAppSelector} from 'src/hooks'
import apiClient from 'src/services/apiClient'
import ImageDialogNew from 'src/pages-new/patient/tabs/images/ImageDialogNew'
import CompareImagesDialogNew from 'src/pages-new/patient/tabs/images/CompareImagesDialogNew'

interface ImagesTabProps {
  patientId: string
}

interface ImageRow {
  _id: string
  bodyPart?: string
  plannedDate?: string
  date?: string
  file?: string
  [key: string]: any
}

interface ImageColumnConfig {
  id: string
  label: string
}

const DEFAULT_COLUMNS: ImageColumnConfig[] = [
  {id: 'bodyPart', label: 'PARTE DEL CORPO'},
  {id: 'plannedDate', label: 'DATA ATTIVITÀ'},
]

const FILTER_CONFIG: FilterItem[] = [
  {id: 'date', label: 'Data', type: 'rangePicker'},
  {id: 'bodyPart', label: 'Parte del corpo', type: 'select', options: BODY_PARTS_OPTIONS},
]

function renderCell(id: string) {
  return (row: ImageRow): React.ReactNode => {
    if (id === 'bodyPart') {
      const label = BODY_PARTS_OPTIONS.find(option => option.value === row.bodyPart)?.label ?? row.bodyPart
      return <Typography fontWeight={600} variant="body2">{label ?? '—'}</Typography>
    }
    if (id === 'plannedDate') {
      return <Typography variant="body2">{row.plannedDate ? moment(row.plannedDate).format('ddd DD MMM YYYY') : '—'}</Typography>
    }
    return <Typography variant="body2">{row[id] ?? '—'}</Typography>
  }
}

/**
 * DataTable has no built-in row-selection support (unlike legacy TableCustom's
 * enableMultiSelect), so selection is implemented locally here: a plain leading
 * checkbox column tracks selected ids in a Set, and `selectedImagesData`
 * accumulates the full row objects for every currently-selected id so the
 * selection (and the data needed to open the compare dialog) survives
 * pagination — replicating legacy Images.tsx's onSelectionChange merge.
 */
export default function ImagesTab({patientId}: ImagesTabProps): JSX.Element {
  const imagesColumnOptions: ImageColumnConfig[] = useAppSelector(
    state => state.pspConfigReducer.data.patientDetailConfiguration?.imagesConfiguration?.columnOptions
  )

  const [rows, setRows] = useState<ImageRow[]>([])
  const [count, setCount] = useState<number>(0)
  const [status, setStatus] = useState<string>(STATUS.PENDING)
  const [statusCount, setStatusCount] = useState<string>(STATUS.PENDING)
  const [pagination, setPagination] = useState<PaginationState>({page: 0, rowsPerPage: 10, order: '-date'})
  const [filters, setFilters] = useState<FilterResult[]>([])

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [selectedImagesData, setSelectedImagesData] = useState<Record<string, any>[]>([])
  const [imageDialogRow, setImageDialogRow] = useState<ImageRow | null>(null)
  const [compareDialogOpen, setCompareDialogOpen] = useState(false)

  const fetchData = useCallback(() => {
    Promise.all([
      apiClient.getPatientImages(patientId, filters, pagination, setStatus),
      apiClient.getPatientImagesCount(patientId, filters, setStatusCount),
    ])
      .then(([images, countResult]) => {
        if (Array.isArray(images)) {
          setRows(images)
        }
        const n = Number(countResult)
        if (!isNaN(n)) setCount(n)
      })
      .catch(() => setStatus(STATUS.REJECTED))
  }, [patientId, filters, pagination])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleFiltersSubmit = (values: FilterResult[]) => {
    setFilters(values)
    setPagination(prev => ({...prev, page: 0}))
  }

  const toggleSelection = (row: ImageRow) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(row._id)) {
        next.delete(row._id)
      } else {
        next.add(row._id)
      }
      return next
    })
    setSelectedImagesData(prev => {
      if (prev.some(image => image._id === row._id)) {
        return prev.filter(image => image._id !== row._id)
      }
      return [...prev, row]
    })
  }

  const handleCloseCompareDialog = () => {
    setCompareDialogOpen(false)
    setSelectedIds(new Set())
    setSelectedImagesData([])
  }

  const columnConfig = imagesColumnOptions?.length > 0 ? imagesColumnOptions : DEFAULT_COLUMNS

  const columns: ColumnDef<ImageRow>[] = [
    {
      key: 'select',
      header: '',
      width: 48,
      isActionColumn: true,
      render: (row: ImageRow) => (
        <Checkbox
          checked={selectedIds.has(row._id)}
          onClick={(e) => e.stopPropagation()}
          onChange={() => toggleSelection(row)}
        />
      ),
    },
    ...columnConfig.map(col => ({
      key: col.id,
      header: col.label,
      render: renderCell(col.id),
      ...(col.id === 'bodyPart' ? {mobileCardHeader: true} : {}),
    })),
    {
      key: 'actions',
      header: '',
      width: 80,
      isActionColumn: true,
      render: (row: ImageRow) => (
        <Box onClick={(e) => e.stopPropagation()} sx={{display: 'flex', gap: 0.5}}>
          <ActionIconButton variant="view" tooltip="Visualizza immagine" onClick={() => setImageDialogRow(row)} />
        </Box>
      ),
    },
  ]

  return (
    <Box>
      {(status === STATUS.REJECTED || statusCount === STATUS.REJECTED) && (
        <InlineMessage
          severity="error"
          onClose={() => { setStatus(STATUS.FULFILLED); setStatusCount(STATUS.FULFILLED) }}
          sx={{mb: 2}}
        >
          {'Errore nel caricare i dati.'}
        </InlineMessage>
      )}

      <Box sx={{display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2}}>
        <Button variant="primary" disabled={selectedIds.size < 2} onClick={() => setCompareDialogOpen(true)}>
          {'Confronta'}
        </Button>
        <Filters config={FILTER_CONFIG} onSubmit={handleFiltersSubmit} />
      </Box>

      <DataTable<ImageRow>
        columns={columns}
        rows={rows}
        keyField="_id"
        loading={status === STATUS.PENDING || statusCount === STATUS.PENDING}
        emptyMessage="Nessuna immagine trovata"
        totalElements={count}
        pagination={pagination}
        onPaginationChange={setPagination}
      />

      <ImageDialogNew
        open={imageDialogRow !== null}
        onClose={() => setImageDialogRow(null)}
        record={imageDialogRow}
      />

      <CompareImagesDialogNew
        open={compareDialogOpen}
        onClose={handleCloseCompareDialog}
        images={selectedImagesData}
      />
    </Box>
  )
}
